/* eslint-disable no-unused-expressions */
import React, {
  useRef,
  useEffect,
  createRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import AsyncReactSelect from 'react-select/async';
import AsyncCreatableReactSelect from 'react-select/async-creatable';
import { components } from 'react-select';
import { Row, Container, Col } from 'react-bootstrap';
import Modal from './styles';
import { useSnackbar } from '../../Snackbar';
import Text from '../../Text';
import Button from '../../Button';

import * as Styled from '../styles';

import { Label } from '../styles';

export default function AsyncSelect({
  label,
  labelColor = '#464e5f',
  error,
  placeholder = 'Selecione...',
  onChange,
  value,
  onSearch,
  multiple,
  creatable,
  inputValue,
  onInputChange,
  onCreateOption,
  negotiator = false,
  horizontal = false,
  isDisabled = false,
  getOptionLabel = option => option.label,
  getOptionValue = option => option.value,
}) {
  const ref = useRef(null);
  const [parsedValue, setParsedValue] = useState([]);
  const [isBig, setIsBig] = useState(false);
  const [show, setShow] = useState(false);
  const [displayedRefs, setDisplayedRefs] = useState([]);
  const [measurements, setMeasurements] = useState({
    tagsWidth: 0,
    tagsWidthAvg: 0,
    width: 0,
  });

  const snackbar = useSnackbar();
  useEffect(() => setParsedValue(value || []), [value]);

  useEffect(() => {
    if (multiple) {
      setDisplayedRefs(prev =>
        Array(parsedValue.length)
          .fill()
          .map((_, i) => prev[i] || createRef())
      );
    }
  }, [multiple, parsedValue]);

  useEffect(() => {
    if (multiple && horizontal) {
      const width = ref.current.offsetWidth;
      const tagsWidth = displayedRefs.reduce(
        (acc, item) => (item.current ? acc + item.current.offsetWidth : acc),
        0
      );
      const tagsWidthCount = displayedRefs.filter(item => item.current).length;
      const tagWidthAvg = tagsWidth / displayedRefs.length - Number(isBig);
      if (
        (tagsWidthCount !== displayedRefs.length &&
          measurements.tagsWidth > tagsWidth) ||
        (displayedRefs.length > 1 && tagsWidth + tagWidthAvg + 30 > width)
      ) {
        setIsBig(true);
      } else {
        setIsBig(false);
      }

      if (tagsWidthCount === displayedRefs.length) {
        setMeasurements({ tagsWidth, width, tagWidthAvg });
      }
    }
  }, [displayedRefs, multiple, horizontal]);

  const timeout = useRef(null);
  const styledSelect = {
    singleValue: provided => ({
      ...provided,
      color: '#494950',
    }),
  };

  function debounceValue(input) {
    if (input.length > 2) {
      return new Promise(resolve => {
        if (timeout.current) {
          clearTimeout(timeout.current);
        }

        timeout.current = setTimeout(async () => {
          let searched = await Promise.resolve(onSearch(input));

          if (show) {
            searched = searched.filter(
              option =>
                !parsedValue.some(
                  v => getOptionValue(option) === getOptionValue(v)
                )
            );
          }

          resolve(searched);
        }, 1000);
      });
    }

    return new Promise(resolve => resolve());
  }

  function addToValue(item) {
    setParsedValue(prev => {
      if (onChange) {
        onChange([...prev, item]);
      }
      return [...prev, item];
    });
  }

  function removeValue(item) {
    setParsedValue(prev => {
      if (onChange) {
        onChange(prev.filter(i => getOptionValue(i) !== getOptionValue(item)));
      }
      return prev.filter(i => getOptionValue(i) !== getOptionValue(item));
    });
  }

  const customComponents = useMemo(
    () => ({
      Control: ({ innerRef, innerProps, children }) => (
        <Styled.Control innerRef={innerRef} {...innerProps} error={error}>
          {children}
        </Styled.Control>
      ),
      ValueContainer: ({ innerRef, innerProps, children }) => (
        <Styled.ValueContainer ref={ref} innerRef={innerRef} {...innerProps}>
          {children}
          {isBig && (
            <Button onClick={() => setShow(true)} variant="secondary" size="sm">
              +
              {displayedRefs.filter(item => item.current).length === 1
                ? displayedRefs.length - 1
                : displayedRefs.length - 2 === 0
                ? displayedRefs.length - 1
                : displayedRefs.length - 2}
            </Button>
          )}
        </Styled.ValueContainer>
      ),
      Option: props => (isBig ? null : <components.Option {...props} />),
      MultiValue: ({ children, components: Components, data, removeProps }) => {
        const index = parsedValue.findIndex(
          item => getOptionValue(item) === getOptionValue(data)
        );

        if (
          isBig &&
          (index >=
            Math.floor(measurements.tagsWidth / measurements.tagWidthAvg - 1) ||
            // TODO: Find some logic to do it better
            index >= 2)
        ) {
          return null;
        }

        return (
          <Styled.MultiValue ref={displayedRefs[index]}>
            <Text type="label">{children}</Text>
            <Styled.MultiValueRemove {...removeProps}>
              <Components.Remove />
            </Styled.MultiValueRemove>
          </Styled.MultiValue>
        );
      },
      Input: props => (isBig ? null : <components.Input {...props} />),
    }),
    [isBig, error, parsedValue, displayedRefs]
  );

  const isValidNewOption = useCallback(
    (option, _, availableOptions) => {
      if (!Array.isArray(parsedValue) || !option || option.length < 3) {
        return false;
      }

      return ![...parsedValue, ...availableOptions].some(
        item => getOptionLabel(item) === option.toLowerCase()
      );
    },
    [parsedValue, getOptionLabel]
  );

  const RenderedReactSelect = useMemo(() => {
    if (creatable) {
      return AsyncCreatableReactSelect;
    }

    return AsyncReactSelect;
  }, [creatable]);

  return (
    <Styled.Container>
      <Modal
        show={show}
        heading={negotiator ? 'Adicione um negociador' : 'Adicione as Tags'}
        scrollable
        animation
        backdrop="static"
        onHide={() => {
          negotiator
            ? parsedValue.length > 0
              ? setShow(false)
              : snackbar.show(
                  <Text>
                    É necessário pelo menos um negociador para salvar as
                    modificações.
                  </Text>,
                  { type: 'error' }
                )
            : setShow(false);
        }}
        body={
          negotiator ? (
            <div>
              <div className="mb-2">
                <Text weight={500}>Negociador: </Text>
              </div>
              <Container fluid>
                <Row>
                  <Col className="d-flex flex-wrap">
                    {multiple &&
                      parsedValue.map(item => (
                        <Styled.MultiValue>
                          <Text type="label">{getOptionLabel(item)}</Text>
                          <Styled.MultiValueRemove
                            onClick={() => removeValue(item)}
                          >
                            <components.MultiValueRemove />
                          </Styled.MultiValueRemove>
                        </Styled.MultiValue>
                      ))}
                  </Col>
                </Row>
              </Container>
              <RenderedReactSelect
                className="pt-3"
                loadOptions={options => debounceValue(options)}
                getOptionLabel={getOptionLabel}
                getOptionValue={getOptionValue}
                onChange={item => addToValue(item)}
                value={null}
                noOptionsMessage={() => <Text>Nenhuma opção encontrada</Text>}
                loadingMessage={() => 'Carregando...'}
                placeholder={placeholder}
                onInputChange={onInputChange}
                inputValue={inputValue}
                onCreateOption={onCreateOption}
                isValidNewOption={isValidNewOption}
                getNewOptionData={(option, optionLabel) => ({
                  id: optionLabel,
                  name: `Criar ${option}`,
                })}
              />
            </div>
          ) : (
            <div>
              <div className="mb-2">
                <Text weight={500}>Tags: </Text>
              </div>
              <Container fluid>
                <Row>
                  <Col className="d-flex flex-wrap">
                    {multiple &&
                      parsedValue.map(item => (
                        <Styled.MultiValue>
                          <Text type="label">{getOptionLabel(item)}</Text>
                          <Styled.MultiValueRemove
                            onClick={() => removeValue(item)}
                          >
                            <components.MultiValueRemove />
                          </Styled.MultiValueRemove>
                        </Styled.MultiValue>
                      ))}
                  </Col>
                </Row>
              </Container>
              <RenderedReactSelect
                className="pt-3"
                loadOptions={options => debounceValue()}
                getOptionLabel={getOptionLabel}
                getOptionValue={getOptionValue}
                onChange={item => addToValue(item)}
                value={null}
                noOptionsMessage={() => <Text>Nenhuma opção encontrada</Text>}
                loadingMessage={() => 'Carregando...'}
                placeholder={placeholder}
                onInputChange={onInputChange}
                inputValue={inputValue}
                onCreateOption={onCreateOption}
                isValidNewOption={isValidNewOption}
                getNewOptionData={(option, optionLabel) => ({
                  id: optionLabel,
                  name: `Criar ${option}`,
                })}
              />
            </div>
          )
        }
        footer={
          negotiator ? (
            <Row style={{ justifyContent: 'flex-end' }}>
              <Button
                variant="primary"
                disabled={!(parsedValue.length > 0)}
                onClick={() => setShow(false)}
              >
                Fechar
              </Button>
            </Row>
          ) : (
            <Row style={{ justifyContent: 'flex-end' }}>
              <Button variant="primary" onClick={() => setShow(false)}>
                Fechar
              </Button>
            </Row>
          )
        }
      />

      {label && (
        <Label color={labelColor} type="label">
          {label}
        </Label>
      )}

      <RenderedReactSelect
        value={parsedValue}
        styles={styledSelect}
        placeholder={placeholder}
        onChange={onChange}
        loadOptions={options => debounceValue()}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        isClearable={!!parsedValue}
        isMulti={multiple}
        isDisabled={isDisabled}
        noOptionsMessage={() => <Text>Nenhuma opção encontrada</Text>}
        loadingMessage={() => 'Carregando...'}
        onInputChange={onInputChange}
        inputValue={inputValue}
        onCreateOption={onCreateOption}
        isValidNewOption={isValidNewOption}
        getNewOptionData={(option, optionLabel) => ({
          id: optionLabel,
          name: `Criar ${option}`,
        })}
        components={customComponents}
      />
      {error && (
        <Styled.ErrorText color="error" type="little">
          {error}
        </Styled.ErrorText>
      )}
    </Styled.Container>
  );
}
